import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const VERDICT_PILL = {
  good: { bg: "#D5EBD9", fg: "#1F5030", label: "Good" },
  moderate: { bg: "#FBE9C4", fg: "#7A5A11", label: "Moderate" },
  avoid: { bg: "#F6C7BE", fg: "#7A2317", label: "Avoid" },
};

const PRODUCT_FILTERS = [
  { key: "", label: "All" },
  { key: "good", label: "🟢 Good" },
  { key: "moderate", label: "🟡 Moderate" },
  { key: "avoid", label: "🔴 Avoid" },
];

const MEAL_FILTERS = [
  { key: "", label: "All" },
  { key: "breakfast", label: "🥣 Breakfast" },
  { key: "lunch", label: "☀️ Lunch" },
  { key: "dinner", label: "🌙 Dinner" },
  { key: "snack", label: "🍎 Snack" },
];

export default function ProductLibrary() {
  const [tab, setTab] = useState("products");
  const navigate = useNavigate();
  return (
    <div className="space-y-6" data-testid="user-product-library">
      <header>
        <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">Community</div>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>Library</h1>
        <p className="text-sm text-[#5C6B61] mt-2 max-w-2xl">
          Browse everything other Shop, Cal &amp; Meal shoppers have scanned or imported. Instant to open —
          no AI delay — and your personal allergens stay in charge.
        </p>
      </header>
      <div className="flex gap-1 rounded-full bg-[#F3F0EA] p-1 w-fit">
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="lib-tab-products">📦 Products</TabBtn>
        <TabBtn active={tab === "recipes"} onClick={() => setTab("recipes")} testid="lib-tab-recipes">🍲 Recipes</TabBtn>
      </div>
      {tab === "products" ? <ProductsTab navigate={navigate} /> : <RecipesTab navigate={navigate} />}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${active ? "bg-white text-[#2A332C] shadow-sm" : "text-[#5C6B61] hover:text-[#2A332C]"}`}
    >
      {children}
    </button>
  );
}

function ProductsTab({ navigate }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [verdict, setVerdict] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/food-scan/library", {
        params: { q: q.trim() || undefined, verdict: verdict || undefined, limit: 60 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the library.");
    } finally {
      setLoading(false);
    }
  }, [q, verdict]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (it) => {
    try {
      const { data } = await api.get(`/food-scan/library/${encodeURIComponent(it.cache_key)}`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the product");
    }
  };

  return (
    <>
      <div className="text-xs text-[#8B9690]" data-testid="library-total">{total} products in the library</div>

      <div className="flex flex-wrap gap-2 items-center mt-3" data-testid="library-controls">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-[220px]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search product name, brand or barcode"
            className="ff-input flex-1 text-sm"
            data-testid="library-search-input"
          />
          <button type="submit" className="rounded-full bg-[#2A332C] text-white text-sm px-4 py-2" data-testid="library-search-btn">Search</button>
        </form>
        <div className="flex gap-1 flex-wrap">
          {PRODUCT_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setVerdict(f.key)}
              data-testid={`library-filter-${f.key || "all"}`}
              className={`text-xs rounded-full px-3 py-1.5 border transition ${verdict === f.key ? "bg-[#3B5A3E] text-white border-[#3B5A3E]" : "bg-white text-[#5C6B61] border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
            >{f.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-4 rounded-2xl border border-[#E5E2DA] bg-white p-10 text-center text-sm text-[#8B9690]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-[#E5E2DA] bg-white p-10 text-center" data-testid="library-empty">
          <div className="text-4xl">📦</div>
          <h3 className="mt-3 font-semibold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
            No scanned products yet — be the first
          </h3>
          <p className="text-sm text-[#5C6B61] mt-2">Every barcode / photo scan lands here so everyone benefits.</p>
          <button onClick={() => navigate("/app/food-scanner")} className="mt-4 ff-btn-primary text-sm" data-testid="library-go-scan">Open Food Scanner</button>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => {
            const pill = VERDICT_PILL[it.verdict] || VERDICT_PILL.moderate;
            return (
              <button
                key={it.cache_key}
                onClick={() => openDetail(it)}
                className="text-left rounded-2xl border border-[#E5E2DA] bg-white p-3 hover:border-[#3B5A3E] transition flex items-center gap-3"
                data-testid={`library-card-${it.cache_key}`}
              >
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0 border border-[#E5E2DA]" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-[#F3F0EA] flex items-center justify-center text-xl shrink-0">🥫</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{it.product_name || "Unknown product"}</div>
                  <div className="text-[11px] text-[#8B9690] truncate">
                    {[it.brand, it.barcode].filter(Boolean).join(" · ") || "—"}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: pill.bg, color: pill.fg }}>{pill.label}</span>
                    <span className="text-[10px] text-[#8B9690]">👥 {it.scan_count || 0}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {detail && <ProductDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function ProductDetailModal({ detail, onClose }) {
  const scan = detail.shared_scan || {};
  const pill = VERDICT_PILL[detail.verdict] || VERDICT_PILL.moderate;
  const allergen = scan.allergen_match;
  const nasties = scan.nasties_match;
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center p-0 sm:p-6" onClick={onClose} data-testid="library-detail-modal">
      <div className="bg-white w-full sm:max-w-xl sm:rounded-3xl rounded-t-3xl max-h-[85vh] overflow-y-auto border border-[#E5E2DA]" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          {detail.image_url ? (
            <img src={detail.image_url} alt="" className="w-full h-48 object-cover" />
          ) : (
            <div className="w-full h-32 bg-[#F3F0EA] flex items-center justify-center text-5xl">🥫</div>
          )}
          <button onClick={onClose} aria-label="Close" data-testid="library-detail-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#2A332C]">✕</button>
          <span className="absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[11px] font-medium" style={{ background: pill.bg, color: pill.fg }}>{pill.label}</span>
        </div>
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">Shared library · scanned {detail.scan_count || 0}×</div>
          <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{detail.product_name || "Unknown product"}</h2>
          <div className="text-sm text-[#5C6B61]">{[detail.brand, detail.barcode].filter(Boolean).join(" · ") || "—"}</div>

          {allergen && allergen.status === "unsafe" && (
            <div className="mt-4 rounded-xl bg-[#FBE5DE] border border-[#F6C7BE] p-3 text-sm text-[#7A2317]" data-testid="library-detail-allergen-unsafe">
              ⚠️ Contains {allergen.hits?.map((h) => h.allergen).join(", ")} — on your "avoid" list.
            </div>
          )}
          {nasties?.hits?.length ? (
            <div className="mt-3 rounded-xl bg-[#FFF4ED] border border-[#F7D9B8] p-3 text-sm text-[#7A2317]" data-testid="library-detail-nasties">
              👀 On your watchlist: {nasties.hits.map((n) => n.name).join(", ")}
            </div>
          ) : null}

          {scan.verdict_reason && <p className="mt-4 text-sm text-[#2A332C]">{scan.verdict_reason}</p>}
          {Array.isArray(scan.ingredients_breakdown) && scan.ingredients_breakdown.length > 0 && (
            <>
              <h3 className="mt-5 text-sm font-semibold text-[#2A332C]">Ingredients</h3>
              <ul className="mt-2 space-y-1 text-sm text-[#5C6B61]">
                {scan.ingredients_breakdown.map((row, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[#2A332C]">{row.name}</span>
                    {row.note ? <span className="text-[#8B9690]">— {row.note}</span> : null}
                  </li>
                ))}
              </ul>
            </>
          )}
          {scan.healthier_swap && (
            <div className="mt-5 rounded-xl bg-[#EDF3E9] border border-[#D5EBD9] p-3 text-sm text-[#1F5030]">
              🌿 Healthier swap: <b>{scan.healthier_swap}</b>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RecipesTab({ navigate }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [mealType, setMealType] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recipes/community", {
        params: { q: q.trim() || undefined, meal_type: mealType || undefined, limit: 60 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load community recipes.");
    } finally {
      setLoading(false);
    }
  }, [q, mealType]);

  useEffect(() => { load(); }, [load]);

  const saveToMine = async (it) => {
    setSavingId(it.community_id);
    try {
      const { data } = await api.post(`/recipes/community/${encodeURIComponent(it.community_id)}/save`);
      if (data.already_saved) toast.info("Already in your Recipes");
      else toast.success(`"${it.title}" added to your Recipes`);
      setItems((xs) => xs.map((x) => x.community_id === it.community_id ? { ...x, already_saved: true, scan_count: (x.scan_count || 0) + (data.already_saved ? 0 : 1) } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that recipe");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <div className="text-xs text-[#8B9690]" data-testid="recipes-total">{total} recipes shared by the community</div>

      <div className="flex flex-wrap gap-2 items-center mt-3" data-testid="recipes-controls">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-[220px]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search dish name or tag"
            className="ff-input flex-1 text-sm"
            data-testid="recipes-search-input"
          />
          <button type="submit" className="rounded-full bg-[#2A332C] text-white text-sm px-4 py-2" data-testid="recipes-search-btn">Search</button>
        </form>
        <div className="flex gap-1 flex-wrap">
          {MEAL_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setMealType(f.key)}
              data-testid={`recipes-filter-${f.key || "all"}`}
              className={`text-xs rounded-full px-3 py-1.5 border transition ${mealType === f.key ? "bg-[#3B5A3E] text-white border-[#3B5A3E]" : "bg-white text-[#5C6B61] border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
            >{f.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-4 rounded-2xl border border-[#E5E2DA] bg-white p-10 text-center text-sm text-[#8B9690]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-[#E5E2DA] bg-white p-10 text-center" data-testid="recipes-empty">
          <div className="text-4xl">🍲</div>
          <h3 className="mt-3 font-semibold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
            No shared recipes yet — be the first to import one
          </h3>
          <p className="text-sm text-[#5C6B61] mt-2">Every recipe you add from a QR, URL or photo appears here for everyone.</p>
          <button onClick={() => navigate("/app/recipes")} className="mt-4 ff-btn-primary text-sm" data-testid="recipes-go-import">Open Recipes</button>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <div
              key={it.community_id}
              className="rounded-2xl border border-[#E5E2DA] bg-white overflow-hidden flex flex-col"
              data-testid={`recipe-card-${it.community_id}`}
            >
              {it.image ? (
                <img src={it.image} alt="" className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-28 bg-[#F3F0EA] flex items-center justify-center text-4xl">🍲</div>
              )}
              <div className="p-3 flex-1 flex flex-col">
                <div className="font-medium text-sm">{it.title}</div>
                <div className="text-[11px] text-[#8B9690] mt-1 flex gap-2 flex-wrap">
                  {it.meal_type && <span>{it.meal_type}</span>}
                  {typeof it.calories_per_serving === "number" && <span>🔥 {Math.round(it.calories_per_serving)} kcal</span>}
                  <span>👥 {it.scan_count || 1}</span>
                </div>
                {(it.tags || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {it.tags.slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] rounded-full bg-[#F3F0EA] px-2 py-0.5 text-[#5C6B61]">{t}</span>
                    ))}
                  </div>
                )}
                <div className="mt-auto pt-3">
                  <button
                    disabled={savingId === it.community_id || it.already_saved}
                    onClick={() => saveToMine(it)}
                    data-testid={`recipe-save-${it.community_id}`}
                    className={`w-full rounded-full text-xs font-medium py-2 transition ${it.already_saved ? "bg-[#EDF3E9] text-[#1F5030] cursor-default" : "bg-[#3B5A3E] text-white hover:bg-[#2F4932] disabled:opacity-60"}`}
                  >
                    {it.already_saved ? "✓ In your Recipes" : savingId === it.community_id ? "Saving…" : "+ Save to my Recipes"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
